import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { Chart } from 'chart.js';
import { AnalyticsService } from '../../../../services/analytics.service';
import { Subscription } from 'rxjs';
import { getDoughnutChartConfig, PARKBOT_COLORS } from '../../../utils/chart-config';
import { AccountService } from '../../../../services/account.service';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-card-vehicle-distribution',
  templateUrl: './card-vehicle-distribution.component.html'
})
export class CardVehicleDistributionComponent implements OnInit, AfterViewInit, OnDestroy {
  distribution: any;
  chart: Chart;
  subscriptions: Subscription[] = [];
  vehicleTypes: any[] = [];
  account: any;
  currentZoneId: string;
  colorMap: Map<string, string> = new Map();
  isLoading = false;
  
  constructor(
    private analyticsService: AnalyticsService,
    private accountService: AccountService,
    private router: Router
  ) { }

  ngOnInit(): void {
    // Subscribe to loading state
    this.subscriptions.push(
      this.analyticsService.loading$.subscribe(loading => {
        this.isLoading = loading;
      })
    );

    this.account = JSON.parse(localStorage.getItem('account'));
    
    // Get current zone ID from URL
    this.updateCurrentZoneId();
    
    // Listen for router events to detect zone changes
    this.subscriptions.push(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe(() => {
        const newZoneId = this.getZoneIdFromUrl();
        if (newZoneId !== this.currentZoneId) {
          this.currentZoneId = newZoneId;
          this.loadVehicleTypes();
        }
      })
    );
    
    // Listen for date change events from analytics service
    this.subscriptions.push(
      this.analyticsService.dateRangeChanged.subscribe(() => {
        this.loadDistributionData();
      })
    );
    
    // Initial load
    this.loadVehicleTypes();
  }
  
  private updateCurrentZoneId(): void {
    this.currentZoneId = this.getZoneIdFromUrl();
  }
  
  private getZoneIdFromUrl(): string {
    const urlParts = window.location.pathname.split('/');
    const zoneId = urlParts[urlParts.length - 1];
    return zoneId !== 'dashboard' ? zoneId : undefined;
  }
  
  private loadVehicleTypes(): void {
    // Get vehicle types from account settings filtered by zone if valid
    this.subscriptions.push(
      this.accountService.getVehicleTypesFromAccount(
        this.account,
        this.currentZoneId
      ).subscribe(vehicleTypes => {
        this.vehicleTypes = vehicleTypes;
        
        // Create consistent color mapping for vehicle types
        this.assignColorsToVehicleTypes();
        
        // After getting vehicle types, load distribution data
        this.loadDistributionData();
      })
    );
  }
  
  private assignColorsToVehicleTypes(): void {
    // Assign consistent colors to vehicle types
    this.colorMap.clear();
    this.vehicleTypes.forEach((vt, index) => {
      if (vt && vt.type) {
        this.colorMap.set(vt.type, PARKBOT_COLORS.colors[index % PARKBOT_COLORS.colors.length]);
      }
    });
  }
  
  loadDistributionData(): void {
    this.subscriptions.push(
      this.analyticsService.getVehicleTypeDistribution()
        .subscribe(data => {
          // Use the flat structure directly
          this.distribution = { ...data };
          
          if (this.chart) {
            this.updateChart();
          } else {
            this.initChart();
          }
        })
    );
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initChart();
    }, 100);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => {
      if (sub) sub.unsubscribe();
    });
    
    if (this.chart) {
      this.chart.destroy();
    }
  }

  private initChart(): void {
    if (!this.distribution) return;
    
    const canvas = document.getElementById('vehicle-distribution-chart') as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Define predefined categories with consistent colors
    const predefinedCategories = [
      { key: 'light_vehicle', name: 'Car', color: '#3B82F6' },
      { key: 'motorcycle', name: 'Motorcycle', color: '#EF4444' },
      { key: 'valet_light_vehicle', name: 'Valet (Guest)', color: '#10B981' },
      { key: 'vip_light_vehicle', name: 'Doctor (Paid)', color: '#F59E0B' },
      { key: 'vip_free_light_vehicle', name: 'Doctor (Free)', color: '#8B5CF6' }
    ];

    let labels = [];
    let data = [];
    let colors = [];
    
    // Build chart data from predefined categories
    predefinedCategories.forEach(category => {
      const count = this.distribution[category.key] || 0;
      if (count > 0) { // Only include categories with data
        labels.push(category.name);
        data.push(count);
        colors.push(category.color);
      }
    });
    
    // If no data, show empty chart with message
    if (labels.length === 0) {
      labels = ['No Data'];
      data = [1];
      colors = ['#f0f0f0']; // Light gray for "no data"
      
      const datasets = [
        {
          data,
          backgroundColor: colors,
          borderWidth: 0
        }
      ];
      
      const config = getDoughnutChartConfig(labels, datasets);
      config.options.plugins.tooltip = {
        ...config.options.plugins.tooltip,
        callbacks: {
          label: () => 'No vehicle data available'
        }
      } as any;
      
      this.chart = new Chart(ctx, config);
      return;
    }
    
    const datasets = [
      {
        data,
        backgroundColor: colors,
        borderWidth: 0
      }
    ];

    const config = getDoughnutChartConfig(labels, datasets);
    
    // Add custom tooltip to show percentages
    config.options.plugins.tooltip = {
      ...config.options.plugins.tooltip,
      callbacks: {
        label: (context) => {
          const label = context.label || '';
          const value = context.raw || 0;
          const total = data.reduce((sum, val) => sum + (val as number), 0);
          const percentage = total > 0 ? Math.round(((value as number) / total) * 100) : 0;
          return `${label}: ${value} (${percentage}%)`;
        }
      }
    } as any;
    
    // Optimize for side-by-side layout
    config.options.maintainAspectRatio = false;
    config.options.plugins.legend = {
      position: 'bottom',
      labels: {
        boxWidth: 8,
        padding: 8,
        usePointStyle: true,
        pointStyle: 'circle'
      }
    };
    
    this.chart = new Chart(ctx, config);
  }

  private updateChart(): void {
    if (!this.chart || !this.distribution) return;
    
    // Define predefined categories with consistent colors
    const predefinedCategories = [
      { key: 'light_vehicle', name: 'Car', color: '#3B82F6' },
      { key: 'motorcycle', name: 'Motorcycle', color: '#EF4444' },
      { key: 'valet_light_vehicle', name: 'Valet (Guest)', color: '#10B981' },
      { key: 'vip_light_vehicle', name: 'Doctor (Paid)', color: '#F59E0B' },
      { key: 'vip_free_light_vehicle', name: 'Doctor (Free)', color: '#8B5CF6' }
    ];

    let labels = [];
    let data = [];
    let colors = [];
    
    // Build chart data from predefined categories
    predefinedCategories.forEach(category => {
      const count = this.distribution[category.key] || 0;
      if (count > 0) { // Only include categories with data
        labels.push(category.name);
        data.push(count);
        colors.push(category.color);
      }
    });
    
    // If no data after filtering, handle empty state
    if (labels.length === 0) {
      this.chart.data.labels = ['No Data'];
      this.chart.data.datasets[0].data = [1];
      this.chart.data.datasets[0].backgroundColor = ['#f0f0f0'];
    } else {
      // Update chart with filtered data
      this.chart.data.labels = labels;
      this.chart.data.datasets[0].data = data;
      this.chart.data.datasets[0].backgroundColor = colors;
    }
    
    this.chart.update();
  }
}
